import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	Experiments,
	IAppDidMountHandler,
	IAppWillLoadPageHandler,
	IPropsStore,
	IStructureAPI,
} from '@wix/thunderbolt-symbols'
import {
	CurrentRouteInfoSymbol,
	ExperimentsSymbol,
	Props,
	SiteFeatureConfigSymbol,
	StructureAPI,
} from '@wix/thunderbolt-symbols'
import type { ITpaSrcBuilder, ITpaContextMapping } from 'feature-tpa-commons'
import { TpaSrcBuilderSymbol, TpaContextMappingSymbol } from 'feature-tpa-commons'
import { name } from './symbols'
import type { ITpaWorker, TpaWorkerSiteConfig } from '@wix/thunderbolt-types'
import type { ICurrentRouteInfo } from 'feature-router'
import { createPromise } from '@wix/thunderbolt-commons'
import { createWorker, isTpaWorker, getWorkerAppDefId, getWorkerApplicationId, getWorkerId } from './tpaWorkerCommon'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const TpaWorkerFactory = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		StructureAPI,
		Props,
		TpaSrcBuilderSymbol,
		TpaContextMappingSymbol,
		CurrentRouteInfoSymbol,
		ExperimentsSymbol,
	],
	(
		{ tpaWorkers }: TpaWorkerSiteConfig,
		structureApi: IStructureAPI,
		props: IPropsStore,
		tpaSrcBuilder: ITpaSrcBuilder,
		tpaContextMapping: ITpaContextMapping,
		currentRouteInfo: ICurrentRouteInfo,
		experiments: Experiments
	): IAppDidMountHandler & ITpaWorker & IAppWillLoadPageHandler => {
		const { resolver: resolveNavigationToNonProtectedPagePromise, promise: waitForNavigationToNonProtectedPage } =
			createPromise()

		const deprecateAppIdExperimentOn = isExperimentOpen(experiments, 'specs.thunderbolt.deprecateAppId')

		return {
			name: 'tpaWorker',
			/* we want to wait for the entire site to be ready before adding the workers, this is why we use appDidMount.
				pageDidLoad is only used to prevent special cases where tpaWorker on site and the starting page is
				a protected page, normally the tpa should not be in the structure at all but because of how tpaWorker
				is inflated it will be and start fire handlers while the site is not really ready yet (for example
				calling getSiteInfo when currentRouteInfo has not been resolved yet)
			 */
			appDidMount() {
				waitForNavigationToNonProtectedPage
					.then(() =>
						Object.entries(tpaWorkers).forEach(([appKey, workerData]) => {
							const maybeAppDefinitionId = deprecateAppIdExperimentOn
								? {
										appDefinitionId: appKey,
									}
								: {}
							const workerId = getWorkerId(appKey)
							createWorker({
								tpaContextMapping,
								props,
								tpaSrcBuilder,
								structureApi,
								workerId,
								...workerData,
								...maybeAppDefinitionId,
							})
						})
					)
					.catch((e) => {
						throw new Error(`Failed to load TPA workers: ${e}`)
					})
			},
			appWillLoadPage() {
				if (currentRouteInfo.getCurrentRouteInfo()) {
					resolveNavigationToNonProtectedPagePromise()
				}
			},
			getWorkerDetails(compId) {
				if (deprecateAppIdExperimentOn) {
					const appDefinitionId: string = getWorkerAppDefId(compId)!
					return { ...tpaWorkers[appDefinitionId!], appDefinitionId }
				} else {
					const applicationId = getWorkerApplicationId(compId)
					return tpaWorkers[applicationId!]
				}
			},
			isTpaWorker,
		}
	}
)
